import { useState, useEffect } from 'react';
import { Plus, Trash2, ToggleLeft, ToggleRight, Pencil, Megaphone } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ message: '', cta_text: 'Book Your First Class', cta_action: '' });

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/announcements`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setAnnouncements(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingId
        ? `${API_BASE}/api/admin/announcements/${editingId}`
        : `${API_BASE}/api/admin/announcements`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowForm(false);
        setEditingId(null);
        setForm({ message: '', cta_text: 'Book Your First Class', cta_action: '' });
        fetchAnnouncements();
      }
    } catch (err) {
      console.error('Failed to save announcement:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      await fetch(`${API_BASE}/api/admin/announcements/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      });
      fetchAnnouncements();
    } catch (err) {
      console.error('Failed to toggle announcement:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await fetch(`${API_BASE}/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchAnnouncements();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({ message: a.message, cta_text: a.cta_text, cta_action: a.cta_action || '' });
    setShowForm(true);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        icon={<Megaphone className="h-6 w-6" />}
        title="Announcements"
        description="Manage homepage announcement ribbons"
        actions={
          <Button onClick={() => { setEditingId(null); setForm({ message: '', cta_text: 'Book Your First Class', cta_action: '' }); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-2" /> New Announcement
          </Button>
        }
      />

      {announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No announcements yet. Create one to display a ribbon on the public homepage.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => (
            <Card key={a.id} className={a.active ? 'border-green-300 bg-green-50' : ''}>
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {a.active && (
                      <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">LIVE</span>
                    )}
                    <p className="font-medium truncate">{a.message}</p>
                  </div>
                  <p className="text-sm text-gray-500">CTA: &quot;{a.cta_text}&quot; {a.cta_action && `→ ${a.cta_action}`}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleToggle(a.id, a.active)} title={a.active ? 'Deactivate' : 'Activate'}>
                    {a.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(a)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit' : 'New'} Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Message</label>
              <Input
                placeholder="e.g. New Programs Available: Yoga & Tai Chi"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Button Text</label>
              <Input
                placeholder="e.g. Book Your First Class"
                value={form.cta_text}
                onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Button Link (optional)</label>
              <Input
                placeholder="e.g. /programs/yoga-internal-arts"
                value={form.cta_action}
                onChange={(e) => setForm({ ...form, cta_action: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Page the button opens. Leave blank to call the dojo.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={!form.message || saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsManager;
